/**
 * Stripe Payment Element Component
 * 
 * Modern Stripe Elements integration using PaymentElement
 * Supports multiple payment methods automatically based on Stripe Dashboard settings
 * 
 * @see https://docs.stripe.com/payments/accept-a-payment?platform=web&ui=elements
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import cx from 'classnames';

import Loader from '../../../Loader';
import { payment } from '../../../../config';
import messages from '../../../../locale/messages';
import { RefreshCw } from 'lucide-react';

import s from './StripePaymentElement.css';

// Load Stripe outside component to avoid recreating on every render
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(payment.stripe.publishableKey);
  }
  return stripePromise;
};

// Validate required reservation data fields
function validateReservationData(data, formatMessage) {
  const errors = [];

  if (!data) {
    return { fields: ['No reservation data provided'], ageError: false };
  }

  // Required fields for reservation
  if (!data.listId) errors.push('listId');
  if (!data.hostId) errors.push('hostId');
  if (!data.guestId) errors.push('guestId');
  if (!data.checkIn) errors.push('checkIn');
  if (!data.currency) errors.push('currency');
  if (!data.firstName) errors.push('firstName');
  if (!data.lastName) errors.push('lastName');
  if (!data.licenseNumber) errors.push('licenseNumber');
  if (!data.message) errors.push('message');
  if (!data.country) errors.push('country');
  if (!data.day || data.month === undefined || data.month === null || !data.year) errors.push('dateOfBirth');

  // Age validation (must be 18+)
  let ageError = false;

  if (data.day && data.month !== undefined && data.month !== null && data.year) {
    const month = Number(data.month) + 1; // month is 0-indexed from select
    const day = Number(data.day);
    const year = Number(data.year);
    const dobDate = new Date(year, month - 1, day);
    const today = new Date();

    let age = today.getFullYear() - dobDate.getFullYear();
    const monthDiff = today.getMonth() - dobDate.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dobDate.getDate())) {
      age--;
    }

    if (age < 18) {
      ageError = true;
    }
  }

  return { fields: errors, ageError };
}

// Inner form component that uses Stripe hooks
function CheckoutForm({ onPaymentSuccess, onPaymentError, returnUrl, isLoading, setIsLoading, formatMessage, reservationData }) {
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    // Validate reservation data before proceeding
    const validation = validateReservationData(reservationData, formatMessage);

    if (validation.ageError) {
      console.error('[StripePaymentElement] Age validation failed - user is under 18');
      onPaymentError(formatMessage({ id: 'app.mustBe18', defaultMessage: 'You must be at least 18 years old to make a reservation' }));
      return;
    }

    if (validation.fields.length > 0) {
      console.error('[StripePaymentElement] Validation errors:', validation.fields);
      onPaymentError(formatMessage({ id: 'app.completeFormFirst', defaultMessage: 'Please complete all required fields before paying' }));
      return;
    }

    setIsLoading(true);

    // Save reservation data to sessionStorage before redirecting
    if (reservationData && typeof window !== 'undefined') {
      sessionStorage.setItem('stripeReservationData', JSON.stringify(reservationData));
    }

    // Ensure returnUrl is valid - construct it if not provided
    const finalReturnUrl = returnUrl || (typeof window !== 'undefined'
      ? `${window.location.origin}/payment/complete?listId=${reservationData?.listId || ''}`
      : null);

    console.log('[StripePaymentElement] Confirming payment with return_url:', finalReturnUrl);

    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: finalReturnUrl,
      },
    });

    // This point will only be reached if there is an immediate error
    // Otherwise, customer is redirected to return_url
    if (error) {
      if (error.type === 'card_error' || error.type === 'validation_error') {
        onPaymentError(error.message);
      } else {
        onPaymentError('An unexpected error occurred.');
      }
      setIsLoading(false);
      // Clear sessionStorage on error
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('stripeReservationData');
      }
    }
  };

  const paymentElementOptions = {
    layout: {
      type: 'tabs',
      defaultCollapsed: false,
    },
    business: {
      name: 'Navega'
    }
  };

  return (
    <div className={s.paymentForm}>
      <PaymentElement
        id="payment-element"
        options={paymentElementOptions}
        className={s.paymentElement}
      />
      <button
        disabled={isLoading || !stripe || !elements}
        type="button"
        onClick={handleSubmit}
        className={s.payButton}
      >
        {isLoading ? (
          <span className={s.spinner}></span>
        ) : (
          formatMessage(messages.payNow)
        )}
      </button>
    </div>
  );
}

class StripePaymentElement extends Component {
  static propTypes = {
    // FX: Now we need listId and targetCurrency instead of amount
    listId: PropTypes.number.isRequired,
    targetCurrency: PropTypes.string.isRequired, // User's preferred currency from Redux
    total: PropTypes.number.isRequired,          // Total in listing currency
    guestServiceFee: PropTypes.number,
    securityDeposit: PropTypes.number,
    currency: PropTypes.string.isRequired,       // Listing's base currency
    reservationId: PropTypes.number,
    guestId: PropTypes.string,
    guestEmail: PropTypes.string,
    listTitle: PropTypes.string,
    onPaymentSuccess: PropTypes.func,
    onPaymentError: PropTypes.func,
    returnUrl: PropTypes.string,
    reservationData: PropTypes.object, // All form data needed to create reservation
  };

  static defaultProps = {
    onPaymentSuccess: () => { },
    onPaymentError: () => { },
    guestServiceFee: 0,
    securityDeposit: 0,
  };

  constructor(props) {
    super(props);
    this.state = {
      clientSecret: null,
      paymentIntentId: null,
      loading: true,
      error: null,
      isProcessing: false,
      // FX (Foreign Exchange) data from backend
      fx: null,
    };
  }

  componentDidMount() {
    this.createPaymentIntent();
  }

  componentDidUpdate(prevProps) {
    // Recreate PaymentIntent if total, currency, or targetCurrency changes
    if (
      prevProps.total !== this.props.total ||
      prevProps.currency !== this.props.currency ||
      prevProps.targetCurrency !== this.props.targetCurrency
    ) {
      this.createPaymentIntent();
    }
  }

  createPaymentIntent = async () => {
    const {
      listId,
      targetCurrency,
      total,
      guestServiceFee,
      processingFee,
      securityDeposit,
      reservationId,
      guestId,
      guestEmail,
      listTitle
    } = this.props;

    this.setState({ loading: true, error: null, fx: null });

    try {
      // FX: Send listId and targetCurrency instead of amount
      // Backend will calculate the converted amount securely
      const response = await fetch('/create-payment-intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          listId,
          targetCurrency,
          total,
          guestServiceFee,
          processingFee,
          securityDeposit,
          reservationId,
          guestId,
          guestEmail,
          listTitle,
        }),
        credentials: 'include',
      });

      const data = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      this.setState({
        clientSecret: data.clientSecret,
        paymentIntentId: data.paymentIntentId,
        fx: data.fx || null, // FX data from backend
        loading: false,
      });

      console.log('[StripePaymentElement] FX data:', data.fx);
    } catch (error) {
      console.error('[StripePaymentElement] Error creating PaymentIntent:', error);
      this.setState({
        error: error.message,
        loading: false,
      });
      this.props.onPaymentError(error.message);
    }
  };

  setIsProcessing = (isProcessing) => {
    this.setState({ isProcessing });
  };

  handlePaymentError = (message) => {
    this.setState({ error: message });
    this.props.onPaymentError(message);
  };

  // Format currency amount with proper decimals
  formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Render FX conversion info box
  renderFxInfo = () => {
    const { fx } = this.state;
    const { formatMessage } = this.props.intl;

    if (!fx) return null;

    // If conversion was applied, show the full FX info
    if (fx.conversionApplied) {
      return (
        <div className={s.fxContainer}>
          <div className={s.fxHeader}>
            <RefreshCw className={s.fxIcon} size={18} />
            <span className={s.fxTitle}>
              <FormattedMessage
                id="fx.currencyConversion"
                defaultMessage="Currency Conversion"
              />
            </span>
          </div>
          <div className={s.fxAmount}>
            {this.formatAmount(fx.paidAmount, fx.paidCurrency)}
          </div>
          <div className={s.fxDetails}>
            <FormattedMessage
              id="fx.equivalentTo"
              defaultMessage="Equivalent to {baseAmount} at rate"
              values={{ baseAmount: this.formatAmount(fx.baseAmount, fx.baseCurrency) }}
            />
            <span className={s.fxRate}>1 {fx.baseCurrency} = {fx.exchangeRate.toFixed(4)} {fx.paidCurrency}</span>
          </div>
        </div>
      );
    }

    // No conversion - show simple total
    return (
      <div className={s.fxNoConversion}>
        <div className={s.fxNoConversionAmount}>
          {this.formatAmount(fx.paidAmount, fx.paidCurrency)}
        </div>
        <div className={s.fxNoConversionLabel}>
          <FormattedMessage
            id="fx.totalToPay"
            defaultMessage="Total to pay"
          />
        </div>
      </div>
    );
  };

  render() {
    const { clientSecret, loading, error, isProcessing, fx } = this.state;
    const { returnUrl } = this.props;
    const { formatMessage } = this.props.intl;

    if (loading) {
      return (
        <div className={s.loaderContainer}>
          <Loader type="text" />
        </div>
      );
    }

    if (error && !clientSecret) {
      return (
        <div className={s.errorContainer}>
          <p className={s.errorMessage}>{error}</p>
          <button onClick={this.createPaymentIntent} className={s.retryButton}>
            <FormattedMessage {...messages.tryAgain} />
          </button>
        </div>
      );
    }

    if (!clientSecret) {
      return null;
    }

    const appearance = {
      theme: 'stripe',
      variables: {
        colorPrimary: '#0056b3',
        colorBackground: '#ffffff',
        colorText: '#1F2937',
        colorDanger: '#df1b41',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        spacingUnit: '4px',
        borderRadius: '8px',
      },
      rules: {
        '.Input': {
          border: '1px solid #E5E7EB',
          boxShadow: 'none',
        },
        '.Input:focus': {
          border: '1px solid #0056b3',
          boxShadow: '0 0 0 1px #0056b3',
        },
        '.Label': {
          fontWeight: '500',
        },
      },
    };

    const options = {
      clientSecret,
      appearance,
      loader: 'auto',
    };

    return (
      <div className={s.container}>
        {error && (
          <div className={s.errorBanner}>
            <span>⚠️</span> {error}
          </div>
        )}
        {/* FX: Show currency conversion info */}
        {this.renderFxInfo()}
        <Elements options={options} stripe={getStripe()}>
          <CheckoutForm
            onPaymentSuccess={this.props.onPaymentSuccess}
            onPaymentError={this.handlePaymentError}
            returnUrl={returnUrl}
            isLoading={isProcessing}
            setIsLoading={this.setIsProcessing}
            formatMessage={formatMessage}
            reservationData={this.props.reservationData}
          />
        </Elements>
      </div>
    );
  }
}

export default injectIntl(withStyles(s)(StripePaymentElement));
